import { Tooltip } from '@chakra-ui/react';
import { HelpIcon, SendChatQuestionIcon } from '@sketchnote/icons';
import { Avatar } from '@sketchnote/shared-components';
import { Dispatch, SetStateAction, useState } from 'react';
import { API } from 'src/api';
import { DotLoader } from 'src/components/loaders';
import { useAuth } from 'src/shared/hooks/use-auth';

import { message } from '../routes/PdfChat';

export const ChatWithPdf = ({
  pdfUrl,
  file,
  fileName,
  fileId,
  messages,
  setMessages,
}: {
  pdfUrl: string;
  file: string;
  fileName: string;
  fileId?: string;
  messages?: message[];
  setMessages: Dispatch<SetStateAction<message[]>>;
}) => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const userDetails = useAuth((s) => s.userDetails);
  const sendQuery = async () => {
    setIsLoading(true);
    try {
      const response = await API.post('/files/pdf-chat/q', {
        id: fileId,
        file: file,
        question: input,
      });
      if (response) {
        setMessages((prev) => {
          prev[0].content = response.data.text;
          return prev;
        });

        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const handleRegenerate = async () => {
    const lastAskedQuestion = messages?.find((i) => i.type === 'question');
    const query = lastAskedQuestion?.content;
    if (query) {
      setIsLoading(true);
      setMessages((prev) =>
        query.length === 0
          ? [...prev]
          : [{ type: 'answer', content: '' }, { type: 'question', content: query }, ...prev]
      );
      try {
        const response = await API.post('/files/pdf-chat/q', {
          id: fileId,
          file: file,
          question: query,
        });
        if (response) {
          setMessages((prev) => {
            prev[0].content = response.data.text;
            return prev;
          });

          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
        console.error(error);
      }
    }
  };

  const handleSubmitQuery = () => {
    setIsLoading(true);
    setMessages((prev) =>
      input.length === 0
        ? [...prev]
        : [{ type: 'answer', content: '' }, { type: 'question', content: input }, ...prev]
    );
    if (input.length !== 0) sendQuery();
    setInput('');
  };
  const isChatInactive = (pdfUrl && pdfUrl.length === 0) || pdfUrl === undefined;
  return (
    <>
      <div
        className={`flex flex-col justify-end w-2/5 text-gray-600 font-medium text-xs border-2 rounded border-gray-200 mb-20, ${
          isChatInactive ? 'bg-gray-200' : 'bg-white'
        }`}
      >
        <div className="flex grow-0 items-center w-full border-b mb-10 shadow-md">
          <h3 className="text-base w-300 font-medium truncate pl-20">{fileName}</h3>
          <HelpIcon className="pr-20" size={20} />
        </div>
        <div className="flex h-full mb-6 px-20 text-base font-regular overflow-auto">
          <ul className="flex flex-col w-full overflow-y-scroll">
            {messages &&
              messages.map((message, index) => (
                <li
                  key={index}
                  className={`flex max-w-xxl p-6 pr-8 rounded-md text-sm float-right mb-8 items-center group ${
                    message.type === 'question'
                      ? 'bg-primary-100 self-end ml-80 text-right'
                      : 'bg-gray-50 self-start mr-80 text-left'
                  }`}
                >
                  {message.type === 'question' ? (
                    <div className="mr-6">
                      <Avatar
                        name={(userDetails && userDetails?.name) || ''}
                        src={userDetails?.avatarUrl}
                        size="sm"
                        type="photo"
                      />
                    </div>
                  ) : (
                    <img
                      className="w-30 h-30 mr-6"
                      src="https://res.cloudinary.com/samvibes/image/upload/v1680691182/onboarding/alex.webp"
                      alt=""
                    />
                  )}
                  {message.type === 'answer' ? (
                    (isLoading && message.content.length === 0) || message.content === '' ? (
                      <DotLoader />
                    ) : (
                      message.content
                    )
                  ) : (
                    message.content
                  )}
                  {message.type === 'answer' && message.content.length > 0 && index === 0 && (
                    <Tooltip label="Regenerate Response">
                      <button
                        onClick={handleRegenerate}
                        className=" group-hover:opacity-100 opacity-0 ml-8 text-gray-500"
                      >
                        ↺
                      </button>
                    </Tooltip>
                  )}
                </li>
              ))}
          </ul>
        </div>
        <div className="flex px-20 items-center pb-10 justify-between">
          <input
            className={`text-base font-regular rounded-md h-40 items-center p-10 w-full mr-10, ${
              isChatInactive ? 'bg-gray-300' : 'bg-gray-50'
            }`}
            disabled={isChatInactive ? true : false}
            placeholder="Ask a question"
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSubmitQuery();
            }}
          />

          <button
            className="ml-10"
            onClick={handleSubmitQuery}
            disabled={isChatInactive ? true : false}
          >
            <SendChatQuestionIcon stroke={isChatInactive ? 'gray' : 'white'} size={42} />
          </button>
        </div>
      </div>
    </>
  );
};
